import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useImportPrcBookMutation,
  type PrcVersionPayload,
  type UploadProgress,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { matchesAccept } from '@shared/lib/pickFiles';
import { AnalysisBar } from '@shared/ui/AnalysisBar';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Progress } from '@shared/ui/Progress';
import { fleetLeading, fleetType, formatCount, typo } from '@entities/fleet';
import { isOldExcel } from '@features/fleet-import';
import { Control } from '@widgets/FleetRegistry/formFields';
import { Actions, FooterStatus, Notice } from '@widgets/FleetRegistry/ui';
import { ComparisonReport } from '../ComparisonReport';
import { describedBy, DrawerTitle, Eyebrow, FormField, HeaderRow, Titles } from '../drawerParts';
import { MODELS, count, formatSize } from '../format';
import { ObjectsField } from '../ObjectsField';
import { Lead, SchemeScope } from '../ui';

/** Книга ЭММ — .xlsx; .xls 97–2003 doc-processor не читает. */
const ACCEPT = '.xlsx,.xlsm';
/** Как `MAX_BOOK_BYTES` в `routes/econ_prc.rs`. */
const MAX_MB = 32;

type Stage =
  | { kind: 'pick' }
  | { kind: 'upload'; fileName: string; loaded: number; total: number }
  | { kind: 'processing'; fileName: string; startedAt: number }
  | { kind: 'done'; payload: PrcVersionPayload };

const Box = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const ProgressBox = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  & > .label {
    margin: 0;
    font-size: ${fleetType.body};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    overflow-wrap: anywhere;
  }

  & > .hint {
    margin: 0;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }
`;

function elapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * «Загрузить книгу ЭММ»: файл уходит с прогрессом, сервер разбирает книгу,
 * переносит цены, ФОТ, параметры и перечни лимитов в черновик, пересчитывает
 * модели справочника и сверяет расчёт с кэшем Excel. Итог — отчёт сверки.
 */
export function ImportDrawer({
  onClose,
  onOpen,
}: {
  onClose: () => void;
  onOpen: (versionId: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'pick' });
  const [label, setLabel] = useState('');
  // Книга обычно посчитана под объект, с которым сейчас работают (шапка).
  const { activeProjectId } = useActiveProjectContext();
  const [objectIds, setObjectIds] = useState<string[]>(() =>
    activeProjectId ? [activeProjectId] : [],
  );
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const pending = useRef<{ abort: () => void } | null>(null);
  const alive = useRef(true);
  const [importBook] = useImportPrcBookMutation();

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      pending.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (stage.kind !== 'processing') return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [stage.kind]);

  useEffect(() => {
    if (stage.kind === 'done') titleRef.current?.focus({ preventScroll: true });
  }, [stage.kind]);

  async function onFile(file: File) {
    setError(null);
    if (file.size === 0) {
      setError('Файл пустой — выберите книгу ЭММ.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Файл больше ${MAX_MB} МБ — сервер такую книгу не примет.`);
      return;
    }
    if (await isOldExcel(file)) {
      if (alive.current) {
        setError(
          'Это книга старого формата .xls (Excel 97–2003). Откройте её в Excel и сохраните как .xlsx: Файл → Сохранить как → Книга Excel.',
        );
      }
      return;
    }
    if (!alive.current) return;
    setStage({ kind: 'upload', fileName: file.name, loaded: 0, total: file.size });
    const onProgress = (p: UploadProgress) => {
      if (!alive.current) return;
      if (p.phase === 'upload') {
        setStage({ kind: 'upload', fileName: file.name, loaded: p.loaded, total: p.total });
      } else {
        setNow(Date.now());
        setStage({ kind: 'processing', fileName: file.name, startedAt: Date.now() });
      }
    };
    const req = importBook({ file, label, project_ids: objectIds, onProgress });
    pending.current = req;
    try {
      const payload = await req.unwrap();
      if (alive.current) setStage({ kind: 'done', payload });
    } catch (err) {
      if (!alive.current) return;
      setStage({ kind: 'pick' });
      setError(apiErrorText(err, 'Не удалось загрузить книгу.'));
    } finally {
      if (pending.current === req) pending.current = null;
    }
  }

  function onDropCapture(e: DragEvent<HTMLDivElement>) {
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0 && !files.some((f) => matchesAccept(f.name, ACCEPT))) {
      setError(`«${files[0]?.name ?? 'файл'}» — нужна книга Excel (.xlsx) «Калькуляция ЭММ».`);
    }
  }

  function close() {
    pending.current?.abort();
    onClose();
  }

  const done = stage.kind === 'done' ? stage.payload : null;
  const percent =
    stage.kind === 'upload' && stage.total > 0 ? Math.round((stage.loaded / stage.total) * 100) : 0;

  return (
    <Drawer open onClose={close} labelledBy={`${uid}-title`} size="lg" initialFocusRef={titleRef}>
      <SchemeScope>
        <DrawerHeader>
          <HeaderRow>
            <Titles>
              <Eyebrow>ПРЦ машино-часа · загрузка из Excel</Eyebrow>
              <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
                {done ? `Черновик «${done.version.label}» создан` : 'Загрузка книги ЭММ'}
              </DrawerTitle>
            </Titles>
            <DrawerCloseButton onClick={close} />
          </HeaderRow>
        </DrawerHeader>

        <DrawerBody>
          {error && (
            <Box>
              <Notice $tone="danger" role="alert">
                <span className="text">{error}</span>
              </Notice>
            </Box>
          )}

          {stage.kind === 'pick' && (
            <>
              <Lead>
                {typo(
                  'Книга «Калькуляция ЭММ» целиком. Цены, ФОТ механизаторов, параметры расчёта и перечни лимитов на ремонт попадут в новый черновик; модели — из справочника техники. ОСНОВА посчитает ПРЦ своими формулами и сверит каждую статью с листом «Расчет машино-час».',
                )}
              </Lead>
              <FormField
                id={`${uid}-label`}
                label="Название черновика"
                hint="Можно не заполнять — назовём по имени файла"
              >
                <Control
                  id={`${uid}-label`}
                  value={label}
                  autoComplete="off"
                  placeholder="Книга ЭММ «Калькуляция ЭММ.xlsx»"
                  aria-describedby={describedBy(`${uid}-label`, true, false)}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </FormField>
              <ObjectsField id={`${uid}-objects`} value={objectIds} onChange={setObjectIds} />
              <Box onDropCapture={onDropCapture}>
                <FileDropZone
                  accept={ACCEPT}
                  primary="Перетащите книгу ЭММ (.xlsx)"
                  hint={`или нажмите, чтобы выбрать файл — до ${MAX_MB} МБ`}
                  onFile={(f) => void onFile(f)}
                />
              </Box>
            </>
          )}

          {stage.kind === 'upload' && (
            <ProgressBox>
              <p className="label">Передаю файл «{stage.fileName}»</p>
              <div
                role="progressbar"
                aria-label="Передача файла"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                aria-valuetext={`${percent} %`}
              >
                <Progress percent={percent} />
              </div>
              <p className="hint">
                {formatSize(stage.loaded)} из {formatSize(stage.total)} · {percent} %
              </p>
            </ProgressBox>
          )}

          {stage.kind === 'processing' && (
            <Box aria-live="polite">
              <AnalysisBar
                title="Разбираю книгу и считаю ПРЦ — обычно до минуты"
                hint={`${stage.fileName} · прошло ${elapsed(now - stage.startedAt)}. Отчёт сверки появится здесь; если закрыть окно, черновик всё равно появится в списке версий.`}
              />
            </Box>
          )}

          {done && (
            <>
              <Box>
                <Notice $tone="success" role="status">
                  <span className="text">
                    {typo(
                      `Черновик посчитан: ${count(done.summary.models, MODELS)}${
                        done.summary.errors > 0
                          ? `, с ошибкой расчёта — ${formatCount(done.summary.errors)}`
                          : ''
                      }. Ничего не утверждено: цены попадут в справочник техники только после утверждения.`,
                    )}
                  </span>
                </Notice>
              </Box>
              {done.version.comparison ? (
                <ComparisonReport comparison={done.version.comparison} />
              ) : (
                <Lead>Отчёта сверки нет — сервер не вернул его.</Lead>
              )}
            </>
          )}
        </DrawerBody>

        <DrawerFooter>
          <FooterStatus role="status">
            {stage.kind === 'upload'
              ? `Передано ${percent} %`
              : stage.kind === 'processing'
                ? 'Сервер разбирает книгу'
                : ''}
          </FooterStatus>
          <Actions>
            {done ? (
              <>
                <Button type="button" $variant="ghost" onClick={onClose}>
                  Закрыть
                </Button>
                <Button type="button" $variant="primary" onClick={() => onOpen(done.version.id)}>
                  Открыть черновик
                </Button>
              </>
            ) : (
              <Button type="button" $variant="ghost" onClick={close}>
                {stage.kind === 'pick'
                  ? 'Отмена'
                  : stage.kind === 'upload'
                    ? 'Прервать загрузку'
                    : 'Закрыть'}
              </Button>
            )}
          </Actions>
        </DrawerFooter>
      </SchemeScope>
    </Drawer>
  );
}
